import { useMemo } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-basic-dist';
import { KeyPress, HighlightZone, PlotSelection } from '../types';
import { Layout, Shape } from 'plotly.js';

// Create Plot component with basic Plotly distribution (smaller bundle)
const Plot = createPlotlyComponent(Plotly);

interface InteractivePlotProps {
    keypresses: KeyPress[];
    highlightZones?: HighlightZone[];
    onSelectionChange?: (selection: PlotSelection | null) => void;
    isLive?: boolean;
}

interface PlotData {
    x: number[];
    y: number[];
    mode: string;
    type: string;
    name: string;
    marker: {
        color: string;
        size: number;
        symbol: string;
    };
    hovertemplate: string;
}

/**
 * Interactive plot showing hold times over time.
 * Simplified Plotly config with horizontal-only selection.
 */
export function InteractivePlot({
    keypresses,
    highlightZones = [],
    onSelectionChange,
    isLive = false
}: InteractivePlotProps) {
    // Separate keypresses by key
    const { key1Data, key2Data } = useMemo(() => {
        const k1: KeyPress[] = [];
        const k2: KeyPress[] = [];

        for (const kp of keypresses) {
            if (kp.key === 'k1' || kp.key === 'z') {
                k1.push(kp);
            } else {
                k2.push(kp);
            }
        }

        return { key1Data: k1, key2Data: k2 };
    }, [keypresses]);

    // Calculate Y axis max for better scaling
    const yMax = useMemo(() => {
        const allHoldTimes = keypresses.map(kp => kp.holdTime);
        if (allHoldTimes.length === 0) return 100;
        const max = Math.max(...allHoldTimes);
        return Math.ceil(max * 1.1 / 10) * 10; // Round up to nearest 10 with 10% padding
    }, [keypresses]);

    // Create plot data traces
    const traces: PlotData[] = useMemo(() => {
        const trace1: PlotData = {
            x: key1Data.map(kp => kp.startTime / 1000),
            y: key1Data.map(kp => kp.holdTime),
            mode: 'markers',
            type: 'scatter',
            name: 'K1',
            marker: {
                color: '#646cff',
                size: 6,
                symbol: 'circle'
            },
            hovertemplate: '%{x:.2f}s: %{y:.0f}ms<extra>K1</extra>'
        };

        const trace2: PlotData = {
            x: key2Data.map(kp => kp.startTime / 1000),
            y: key2Data.map(kp => kp.holdTime),
            mode: 'markers',
            type: 'scatter',
            name: 'K2',
            marker: {
                color: '#ff66ab',
                size: 6,
                symbol: 'circle'
            },
            hovertemplate: '%{x:.2f}s: %{y:.0f}ms<extra>K2</extra>'
        };

        return [trace1, trace2];
    }, [key1Data, key2Data]);

    // Create highlight zone shapes
    const shapes: Partial<Shape>[] = useMemo(() => {
        return highlightZones.map(zone => {
            let fillcolor: string;
            switch (zone.color) {
                case 'red':
                    fillcolor = 'rgba(255, 66, 66, 0.15)';
                    break;
                case 'orange':
                    fillcolor = 'rgba(255, 165, 0, 0.15)';
                    break;
                default:
                    fillcolor = 'rgba(66, 255, 66, 0.1)';
            }

            return {
                type: 'rect' as const,
                xref: 'x' as const,
                yref: 'paper' as const,
                x0: zone.start,
                x1: zone.end,
                y0: 0,
                y1: 1,
                fillcolor,
                line: { width: 0 },
                layer: 'below' as const
            };
        });
    }, [highlightZones]);

    // Layout configuration - simplified and constrained
    const layout: Partial<Layout> = useMemo(() => ({
        xaxis: {
            title: { text: 'Time (s)', font: { size: 11, color: '#666' }, standoff: 5 },
            color: '#888',
            gridcolor: '#2a2a2a',
            zerolinecolor: '#333',
            tickfont: { size: 10 }
        },
        yaxis: {
            title: { text: 'Hold (ms)', font: { size: 11, color: '#666' }, standoff: 5 },
            color: '#888',
            gridcolor: '#2a2a2a',
            zerolinecolor: '#333',
            tickfont: { size: 10 },
            range: [0, yMax], // Constrain Y min to 0
            fixedrange: false,
            rangemode: 'nonnegative' // Don't allow Y to go below 0
        },
        paper_bgcolor: 'transparent',
        plot_bgcolor: '#151515',
        font: { color: '#888', size: 10 },
        legend: {
            x: 1,
            y: 1,
            xanchor: 'right',
            bgcolor: 'transparent',
            font: { size: 10 }
        },
        margin: { l: 45, r: 10, t: 10, b: 35 },
        shapes,
        dragmode: 'select', // Default to selection mode
        selectdirection: 'h', // Horizontal only selection
        hovermode: 'closest',
        hoverlabel: {
            bgcolor: '#333',
            bordercolor: '#555',
            font: { size: 11, color: '#fff' }
        }
    }), [yMax, shapes]);

    // Handle selection events
    const handleRelayout = (event: Record<string, unknown>) => {
        if (onSelectionChange) {
            if (event['xaxis.range[0]'] !== undefined && event['xaxis.range[1]'] !== undefined) {
                onSelectionChange({
                    startTime: event['xaxis.range[0]'] as number,
                    endTime: event['xaxis.range[1]'] as number
                });
            } else if (event['xaxis.autorange']) {
                onSelectionChange(null);
            }
        }
    };

    const handleSelected = (event: { range?: { x: number[] } } | undefined) => {
        if (onSelectionChange && event?.range?.x) {
            onSelectionChange({
                startTime: event.range.x[0],
                endTime: event.range.x[1]
            });
        }
    };

    if (keypresses.length === 0) {
        return (
            <div className="plot-placeholder">
                <p>{isLive ? 'Press keys to see data' : 'No data'}</p>
            </div>
        );
    }

    return (
        <Plot
            data={traces as Plotly.Data[]}
            layout={layout}
            config={{
                responsive: true,
                displayModeBar: true,
                displaylogo: false,
                // Only show essential buttons
                modeBarButtonsToRemove: [
                    'lasso2d',
                    'select2d',
                    'autoScale2d',
                    'hoverClosestCartesian',
                    'hoverCompareCartesian',
                    'toggleSpikelines',
                    'toImage'
                ] as Plotly.ModeBarDefaultButtons[],
                modeBarButtonsToAdd: [],
                scrollZoom: false // Disable scroll zoom to prevent accidental zooms
            }}
            style={{ width: '100%', height: '100%', minHeight: '250px' }}
            onRelayout={handleRelayout}
            onSelected={handleSelected}
            useResizeHandler={true}
        />
    );
}

export default InteractivePlot;